/**
 * Shared helpers for teacher profile pictures from API rows.
 * Field contract: camelCase `profilePic` (ASP.NET); older clients may send `ProfilePic`.
 */

/**
 * @param {unknown} value
 * @returns {string|undefined}
 */
export function normalizeProfilePicSrc(value) {
  if (value == null) return undefined;
  const raw = String(value).trim();
  if (!raw) return undefined;

  if (
    raw.startsWith("data:") ||
    raw.startsWith("blob:") ||
    /^https?:\/\//i.test(raw) ||
    raw.startsWith("/")
  ) {
    return raw;
  }

  // Bare base64 from mobile registration storage
  const compact = raw.replace(/\s+/g, "");
  if (compact.length > 64 && /^[A-Za-z0-9+/=]+$/.test(compact)) {
    return `data:image/jpeg;base64,${compact}`;
  }

  return raw;
}

/**
 * @param {object|string|null|undefined} source Teacher row or raw pic string
 * @returns {string|undefined}
 */
export function resolveProfilePic(source) {
  if (source == null) return undefined;
  if (typeof source === "string") return normalizeProfilePicSrc(source);

  const hasFlag = source.hasProfilePic ?? source.HasProfilePic;
  if (hasFlag === false) return undefined;

  const raw = source.profilePic ?? source.ProfilePic ?? "";
  return normalizeProfilePicSrc(raw);
}

/**
 * @param {object|null|undefined} row
 * @returns {string}
 */
export function teacherDisplayName(row) {
  if (!row || typeof row !== "object") return "";
  const full =
    row.fullName ??
    row.FullName ??
    row.teacherName ??
    row.TeacherName ??
    "";
  if (String(full).trim()) return String(full).trim();

  const first = row.name ?? row.Name ?? row.firstName ?? "";
  const last = row.preName ?? row.PreName ?? row.lastName ?? "";
  return [first, last].filter(Boolean).join(" ").trim();
}

/**
 * @param {object|string|null|undefined} rowOrName
 * @returns {string}
 */
export function teacherInitials(rowOrName) {
  const label =
    typeof rowOrName === "string"
      ? rowOrName
      : teacherDisplayName(rowOrName) ||
        String(rowOrName?.preName ?? rowOrName?.name ?? "").trim();

  const parts = String(label || "?").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((p) => (p[0] || "").toUpperCase())
    .join("") || "?";
}

/**
 * Escape for HTML text / attribute contexts used in Leaflet popup markup.
 * @param {unknown} value
 */
export function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Compact avatar HTML for Leaflet / Select2 templates.
 * @param {object|null|undefined} row
 * @param {number} [sizePx=28]
 * @returns {string}
 */
export function buildTeacherAvatarHtml(row, sizePx = 28) {
  const src = resolveProfilePic(row);
  const initials = escapeHtml(teacherInitials(row));
  const size = Number.isFinite(sizePx) && sizePx > 0 ? sizePx : 28;
  const style = `width:${size}px;height:${size}px;`;

  if (!src) {
    return `<span class="map-popup-avatar map-popup-avatar--fallback" style="${style}" aria-hidden="true">${initials}</span>`;
  }

  const safeSrc = escapeHtml(src);
  return `<img class="map-popup-avatar" src="${safeSrc}" alt="" width="${size}" height="${size}" loading="lazy" decoding="async" onerror="this.onerror=null;this.replaceWith(Object.assign(document.createElement('span'),{className:'map-popup-avatar map-popup-avatar--fallback',style:'${style}',textContent:'${initials}'}))" />`;
}
